/**
 * Prodajna mjesta block — Leaflet map (Carto Positron tiles, white/grey to
 * match the site) + scrollable city list where clicking a city expands its
 * dealers inline (accordion), directly below that row.
 *
 * Data comes from the `data-cities` JSON attribute on `.prodajna-mjesta__map`
 * (one entry per city: slug, city, lat, lng, dealers[]). Same
 * "enqueue Leaflet only when the block is on the page + wait for window.L"
 * pattern as the existing Contact Map block (resources/js/blocks/contactMap.js),
 * which also uses Carto Positron — matching tile source for visual consistency
 * between the two Leaflet maps on this page.
 */

const PIN_SIZE = 28;

function buildPin(L) {
	const html = '<span class="prodajna-mjesta__pin-dot"></span>';
	return L.divIcon({
		html,
		className: 'prodajna-mjesta__pin-wrap',
		iconSize: [PIN_SIZE, PIN_SIZE],
		iconAnchor: [PIN_SIZE / 2, PIN_SIZE],
		popupAnchor: [0, -PIN_SIZE],
	});
}

function escapeHtml(value) {
	const div = document.createElement('div');
	div.textContent = value == null ? '' : String(value);
	return div.innerHTML;
}

function cleanTel(phone) {
	return String(phone).replace(/[\s()-]/g, '');
}

// Compact inline row (not a boxed "card") — name bold, address, tel/mail links.
function dealerRowHtml(dealer) {
	const parts = [`<div class="prodajna-mjesta__dealer">`];
	parts.push(`<p class="prodajna-mjesta__dealer-name">${escapeHtml(dealer.name)}</p>`);

	if (dealer.address) {
		parts.push(`<p class="prodajna-mjesta__dealer-line">${escapeHtml(dealer.address)}</p>`);
	}

	(dealer.phones || []).forEach((phone) => {
		if (!phone) return;
		parts.push(
			`<p class="prodajna-mjesta__dealer-line"><a href="tel:${escapeHtml(cleanTel(phone))}">${escapeHtml(phone)}</a></p>`
		);
	});

	(dealer.emails || []).forEach((email) => {
		if (!email) return;
		parts.push(
			`<p class="prodajna-mjesta__dealer-line"><a href="mailto:${escapeHtml(email)}">${escapeHtml(email)}</a></p>`
		);
	});

	parts.push('</div>');
	return parts.join('');
}

function popupHtml(city) {
	const n = (city.dealers || []).length;
	const word = n === 1 ? 'prodajno mjesto' : n >= 2 && n <= 4 ? 'prodajna mjesta' : 'prodajnih mjesta';
	return `<strong>${escapeHtml(city.city)}</strong><br>${n} ${word}`;
}

function initSingle(root) {
	if (!window.L) {
		return;
	}

	let cities = [];
	try {
		cities = JSON.parse(root.dataset.cities || '[]');
	} catch (e) {
		cities = [];
	}

	if (!cities.length) {
		return;
	}

	const map = window.L.map(root, {
		center: [44.2, 17.7],
		zoom: 7,
		scrollWheelZoom: false,
		zoomControl: true,
	});

	// Carto "Positron" — minimal white/grey basemap matching the BEPRO B&W
	// aesthetic (same tile source as the existing Contact Map block). Free,
	// no API key, retina-ready. A grayscale CSS filter on .leaflet-tile-pane
	// (see _prodajna-mjesta.scss) is layered on top as a fallback so the map
	// still reads monochrome even if this tile source is ever unreachable.
	window.L.tileLayer('https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png', {
		attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &copy; <a href="https://carto.com/attributions">CARTO</a>',
		subdomains: 'abcd',
		maxZoom: 20,
	}).addTo(map);

	const markers = {};
	const citiesBySlug = {};
	const bounds = [];

	cities.forEach((city) => {
		const lat = parseFloat(city.lat);
		const lng = parseFloat(city.lng);

		if (Number.isNaN(lat) || Number.isNaN(lng)) {
			return;
		}

		citiesBySlug[city.slug] = city;

		const marker = window.L.marker([lat, lng], {
			icon: buildPin(window.L),
			title: city.city || '',
		}).addTo(map);

		marker.bindPopup(popupHtml(city), { maxWidth: 220 });
		markers[city.slug] = marker;
		bounds.push([lat, lng]);
	});

	if (bounds.length > 1) {
		map.fitBounds(bounds, { padding: [24, 24], maxZoom: 9 });
	}

	map.on('click focus', () => map.scrollWheelZoom.enable());
	map.on('mouseout', () => map.scrollWheelZoom.disable());

	const section = root.closest('.prodajna-mjesta');
	if (!section) {
		return;
	}

	const rows = section.querySelectorAll('[data-prodajna-mjesta-row]');

	// Persistent hover-style feedback on the pin matching the expanded city
	// (round 4 — pins are black, so this is the only visual cue on the map
	// itself besides the popup). Pass null to clear every pin.
	function setActivePin(slug) {
		Object.entries(markers).forEach(([s, marker]) => {
			const el = marker.getElement ? marker.getElement() : null;
			if (el) {
				el.classList.toggle('is-active', s === slug);
			}
		});
	}

	function closeRow(row) {
		const btn = row.querySelector('[data-prodajna-mjesta-city]');
		const panelEl = row.querySelector('[data-prodajna-mjesta-city-panel]');

		row.classList.remove('is-active');
		if (btn) {
			btn.setAttribute('aria-expanded', 'false');
		}
		if (panelEl) {
			panelEl.hidden = true;
			panelEl.innerHTML = '';
		}
	}

	function openRow(row, city) {
		const btn = row.querySelector('[data-prodajna-mjesta-city]');
		const panelEl = row.querySelector('[data-prodajna-mjesta-city-panel]');

		row.classList.add('is-active');
		if (btn) {
			btn.setAttribute('aria-expanded', 'true');
		}
		if (panelEl) {
			panelEl.innerHTML = (city.dealers || []).map(dealerRowHtml).join('');
			panelEl.hidden = false;
		}
	}

	// showCity() is the single entry point for both the list (click a row —
	// toggles open/closed) and the map (click a pin — always opens, never
	// closes what's already open). Only one city is expanded at a time.
	function showCity(slug, { pan = true, toggle = false } = {}) {
		const city = citiesBySlug[slug];
		const row = section.querySelector(`[data-prodajna-mjesta-row="${slug}"]`);
		if (!city || !row) {
			return;
		}

		const wasOpen = row.classList.contains('is-active');

		rows.forEach((r) => {
			if (r !== row) {
				closeRow(r);
			}
		});

		if (toggle && wasOpen) {
			closeRow(row);
			setActivePin(null);
			const marker = markers[slug];
			if (marker) {
				marker.closePopup();
			}
			return;
		}

		openRow(row, city);
		setActivePin(slug);
		row.scrollIntoView({ block: 'nearest' });

		const marker = markers[slug];
		if (marker) {
			if (pan) {
				map.setView(marker.getLatLng(), Math.max(map.getZoom(), 9), { animate: true });
			}
			marker.openPopup();
		}
	}

	Object.entries(markers).forEach(([slug, marker]) => {
		marker.on('click', () => showCity(slug, { pan: false, toggle: false }));
	});

	rows.forEach((row) => {
		const btn = row.querySelector('[data-prodajna-mjesta-city]');
		if (!btn) {
			return;
		}
		btn.addEventListener('click', () => {
			showCity(btn.dataset.prodajnaMjestaCity, { pan: true, toggle: true });
		});
	});
}

function waitForLeaflet(callback, attempts = 40) {
	if (window.L) {
		callback();
		return;
	}
	if (attempts <= 0) {
		return;
	}
	setTimeout(() => waitForLeaflet(callback, attempts - 1), 100);
}

export default function prodajnaMjesta() {
	const blocks = document.querySelectorAll('[data-prodajna-mjesta-map]');
	if (!blocks.length) {
		return;
	}

	waitForLeaflet(() => {
		blocks.forEach((root) => initSingle(root));
	});
}
